import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [snapshots, setSnapshots] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load stats
      const statsData = await api.getStats();
      setStats(statsData);

      // Load available dates
      const datesData = await api.getSnapshotDates();
      setDates(datesData);

      // Load snapshots for selected date or latest
      let snapshotsData;
      if (selectedDate) {
        snapshotsData = await api.getSnapshotsByDate(selectedDate);
      } else {
        snapshotsData = await api.getLatestSnapshots();
        if (datesData.length > 0 && !selectedDate) {
          setSelectedDate(datesData[0].snapshot_date);
        }
      }

      setSnapshots(snapshotsData);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateSnapshot = async () => {
    if (!window.confirm('Create a new snapshot? This will collect current data from Azure.')) {
      return;
    }

    try {
      setLoading(true);
      await api.createSnapshot();
      await loadData();
      alert('Snapshot created successfully!');
    } catch (err) {
      alert('Failed to create snapshot: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const chartData = snapshots.map(s => ({
    name: s.customer_name,
    GB: parseFloat(s.gbytes.toFixed(2)),
    blobs: s.blob_count
  })).sort((a, b) => b.GB - a.GB);

  if (loading && !snapshots.length) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <nav className="navbar">
        <div className="navbar-content">
          <h1 className="navbar-title">AzureUsage Dashboard</h1>
          <div className="navbar-actions">
            <span className="navbar-user">{user?.email}</span>
            <button className="button button-secondary" onClick={() => navigate('/settings')}>
              Settings
            </button>
            <button className="button button-secondary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container">
        {error && <div className="error">{error}</div>}

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.customerCount}</div>
              <div className="stat-label">Customers</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalGB} GB</div>
              <div className="stat-label">Total Storage</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalBlobs}</div>
              <div className="stat-label">Total Blobs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.averageGBPerCustomer} GB</div>
              <div className="stat-label">Avg per Customer</div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="card controls-card">
          <div className="controls">
            <div className="control-group">
              <label className="label">Snapshot Date:</label>
              <select
                className="input date-select"
                value={selectedDate || ''}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                {dates.map(d => (
                  <option key={d.snapshot_date} value={d.snapshot_date}>
                    {new Date(d.snapshot_date).toLocaleDateString('cs-CZ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="control-actions">
              <button
                className="button button-primary"
                onClick={handleCreateSnapshot}
                disabled={loading}
              >
                Create Snapshot
              </button>
              <button
                className="button button-secondary"
                onClick={() => setShowInviteModal(true)}
              >
                Invite User
              </button>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="card chart-card">
          <h2 className="card-title">Storage Usage by Customer</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis label={{ value: 'GB', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="GB" fill="#667eea" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">
              <p>No data available. Create a snapshot to get started.</p>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="card table-card">
          <h2 className="card-title">Detailed Usage</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Storage (GB)</th>
                  <th>Blob Count</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map(item => (
                  <tr key={item.name}>
                    <td>{item.name}</td>
                    <td>{item.GB}</td>
                    <td>{item.blobs}</td>
                    <td>{selectedDate ? new Date(selectedDate).toLocaleDateString('cs-CZ') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal onClose={() => setShowInviteModal(false)} />
      )}
    </div>
  );
}

function InviteModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    setLoading(true);

    try {
      const result = await api.invite(email);
      setSuccess(result);
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Invite New User</h2>
        <form onSubmit={handleSubmit} className="invite-form">
          <div className="form-group">
            <label className="label">Email Address</label>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          {error && <div className="error">{error}</div>}
          
          {success && (
            <div className="success">
              <p>Invitation sent successfully!</p>
              <div className="invitation-url">
                <label>Invitation URL:</label>
                <input
                  type="text"
                  className="input"
                  value={success.invitationUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="submit" className="button button-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
            <button type="button" className="button button-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Dashboard;

